import {
  useDeleteAssessmentMutation,
  useUpdateAssessmentMutation,
} from '@/app/redux/endpoints/assessments';
import { useUpdateStudentMutation } from '@/app/redux/endpoints/students';
import { useAppDispatch } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { Assessment, AssessmentStudent } from '@/common/types/assessment';
import { ControllerType } from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { setStudentCreationModalOpen } from '@/app/redux/slices/modalsSlice';
import { LearnerMultiSelect } from '@/dashboard/MultiSelect';
import { MouseLeftClickIcon } from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import { Checkbox } from '@shadcn/components/checkbox';
import { Label } from '@shadcn/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { Info, Keyboard } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import styled from 'styled-components';

interface AssessmentDetailsProps {
  assessment: Assessment;
}
export default function AssessmentDetails({
  assessment,
}: AssessmentDetailsProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { students, studentsById } = useStudents();
  const [updateStudent] = useUpdateStudentMutation();
  const [updateAssessment] = useUpdateAssessmentMutation();
  const [deleteAssessment] = useDeleteAssessmentMutation();

  const editingDisabled = useMemo(() => {
    return Object.values(assessment.students).some((s) => !!s.startedAt);
  }, [assessment]);

  async function handleSelectStudent(studentId: string) {
    let updatedStudentIds = assessment.studentIds;
    let updatedStudents = assessment.students;

    const existing = assessment.students[studentId];
    if (existing) {
      // remove student
      updatedStudentIds = assessment.studentIds.filter(
        (id) => id !== studentId,
      );
      updatedStudents = Object.fromEntries(
        Object.entries(assessment.students).filter(
          ([key]) => key !== studentId,
        ),
      );
    } else {
      // add student
      updatedStudentIds = [...assessment.studentIds, studentId];
      updatedStudents = {
        ...assessment.students,
        [studentId]: {
          startedAt: null,
          completedAt: null,
          placementLevel: null,
          phase: assessment.phase,
          control: assessment.control,
          placeAtAssessedLevel: assessment.placeAtAssessedLevel,
          doScreener: assessment.doScreener,
        },
      };
    }

    // ensure changes actually occurred
    if (
      updatedStudentIds.length === assessment.studentIds.length ||
      updatedStudents.length === assessment.students.length
    ) {
      return;
    }

    // delete assessment if it was the last student
    if (updatedStudentIds.length === 0) {
      await handleDeleteAssessment();
      return;
    }

    try {
      if (!currentOrganisationId) throw new Error('Missing org ID');

      await updateStudent({
        orgId: currentOrganisationId,
        studentId,
        updates: {
          assessmentId: existing ? null : assessment.id,
        },
      });

      await updateAssessment({
        orgId: currentOrganisationId,
        assessmentId: assessment.id,
        updates: {
          studentIds: updatedStudentIds,
          students: updatedStudents,
        },
      }).unwrap();
      trackMixpanelEvent(`Assessment students updated`, {
        assessmentId: assessment.id,
        newStudentIds: updatedStudentIds,
      });
      toast.success('Assessment updated');
    } catch (error) {
      console.error('Failed to update assessment', error);
      toast.error('Failed to update assessment', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  async function handleDeleteAssessment() {
    try {
      if (!currentOrganisationId) throw new Error('Missing org ID');
      const studentIds =
        assessment.studentIds ?? Object.keys(assessment.students);
      await deleteAssessment({
        orgId: currentOrganisationId,
        assessmentId: assessment.id,
      }).unwrap();
      for (const studentId of studentIds) {
        await updateStudent({
          orgId: currentOrganisationId,
          studentId,
          updates: { assessmentId: null },
        });
      }
      trackMixpanelEvent('Assessment deleted', {
        assessmentId: assessment.id,
      });
      navigate('/dashboard/assessments');
      toast.success('Assessment deleted');
    } catch (error) {
      console.error('Failed to delete assessment:', error);
      toast.error('Failed to delete assessment', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  function handleFilterStudents(searchTerm: string) {
    return (students || []).filter((student) => {
      const key = `${student.studentFirstName} ${student.studentSurname} ${student.spellcastUsername}`;
      const terms = searchTerm.split(' ');
      return terms.every((term) =>
        key.toLowerCase().includes(term.toLowerCase()),
      );
    });
  }

  async function handleUpdateAssessment<
    K extends keyof Pick<
      Assessment,
      'phase' | 'control' | 'placeAtAssessedLevel'
    >,
  >(field: K, newValue: (typeof assessment)[K]) {
    if (newValue === assessment[field]) return;

    try {
      await updateAssessment({
        orgId: currentOrganisationId!,
        assessmentId: assessment.id,
        updates: {
          [field]: newValue,
          students: Object.entries(assessment.students).reduce(
            (acc, [id, s]) => {
              acc[id] = { ...s, [field]: newValue };
              return acc;
            },
            {} as Record<string, AssessmentStudent>,
          ),
        },
      }).unwrap();
      trackMixpanelEvent(`Assessment ${field} updated`, {
        assessmentId: assessment.id,
        [`new${field.charAt(0).toUpperCase()}${field.slice(1)}`]: newValue,
      });
      toast.success('Assessment updated');
    } catch (error) {
      console.error('Failed to update assessment', error);
      toast.error('Failed to update assessment', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  return (
    <div className="flex flex-col gap-6 m-4">
      {editingDisabled && (
        <Alert className="flex items-center gap-2 p-2 text-muted-foreground">
          <Info className="ml-1 size-4" color="hsl(var(--muted-foreground))" />
          <span className="ml-2">
            Assessment settings can't be updated once learners have started it,
            but you can still add or remove learners.
          </span>
        </Alert>
      )}

      <InputContainer>
        <Label htmlFor="learner-select">Learner(s)</Label>
        <LearnerMultiSelect
          items={students || []}
          selectedIds={assessment.studentIds.filter(
            (id) => !!studentsById.get(id),
          )}
          onSelect={handleSelectStudent}
          onFilter={handleFilterStudents}
          getItemDisabled={(s) =>
            !assessment.studentIds.includes(s.id) && !!s.assessmentId
          }
          getDisabledReason={(s) => {
            if (s.assessmentId) {
              return `${s.studentFirstName} ${s.studentSurname} already has an active assessment`;
            }
            return '';
          }}
          getConfirmRemoveDescription={(s) => {
            const desc = [
              `If ${s.studentFirstName} ${s.studentSurname} has already completed this assessment, their results will still be viewable from their activity feed.`,
            ];
            if (assessment.studentIds.length === 1) {
              desc.unshift(
                `${s.studentFirstName} ${s.studentSurname} is the only learner in this assessment. Removing them will also delete this assessment. This cannot be undone.`,
              );
            }
            return desc;
          }}
          onAddItem={() => dispatch(setStudentCreationModalOpen(true))}
        />
      </InputContainer>

      <InputContainer>
        <Label htmlFor="content">Assessment Content</Label>
        <Select
          value={assessment.phase.toString()}
          onValueChange={(value) =>
            handleUpdateAssessment('phase', Number(value))
          }
          disabled={editingDisabled}
        >
          <SelectTrigger id="phase" className="min-w-[150px]">
            {assessment.phase === 0 ? (
              <span className="font-medium">Screener (Phases 1-3)</span>
            ) : (
              <span className="font-medium">Phase {assessment.phase}</span>
            )}
          </SelectTrigger>
          <SelectContent className="w-[var(--radix-select-trigger-width)]">
            <SelectItem value={'0'} className="max-w-md">
              <span className="font-medium">Screener (Phases 1-3)</span>
              <br />
              <span className="text-xs text-muted-foreground">
                Start with a screener to assess all of Phases 1-3 at once.
              </span>
            </SelectItem>
            {scopeAndSequence.phases.map((phase) => (
              <SelectItem
                key={phase.phase}
                value={phase.phase.toString()}
                className="max-w-md"
                disabled={phase.phase === 5}
              >
                <span className="font-medium">Phase {phase.phase}</span>
                {phase.phase === 5 && (
                  <span className="text-xs ml-1">(Coming soon)</span>
                )}
                <br />
                <span className="text-xs text-muted-foreground">
                  {phase.description}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ">
          Select the phase of the Scope & Sequence to assess.
        </span>
      </InputContainer>

      <InputContainer>
        <Label htmlFor="control">Control Type</Label>
        <Select
          value={assessment.control}
          onValueChange={(value) =>
            handleUpdateAssessment('control', value as ControllerType)
          }
          disabled={editingDisabled}
        >
          <SelectTrigger id="control">
            <SelectValue placeholder="Select control type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ControllerType.KEYBOARD}>
              <span className="flex items-center gap-2">
                <Keyboard size={16} />
                Typing
              </span>
            </SelectItem>
            <SelectItem value={ControllerType.CLICK}>
              <span className="flex items-center gap-2">
                <MouseLeftClickIcon size={16} />
                Click to Place
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          Select the control type to use for the assessment{' '}
          <strong>(we advise Click to Place for tablets)</strong>.
        </span>
      </InputContainer>

      <InputContainer>
        <CheckboxContainer>
          <Checkbox
            id="place-at-assessed-level"
            checked={assessment.placeAtAssessedLevel}
            onCheckedChange={(checked) =>
              handleUpdateAssessment(
                'placeAtAssessedLevel',
                checked === 'indeterminate' ? false : checked,
              )
            }
            disabled={editingDisabled}
          />
          <Label htmlFor="place-at-assessed-level">
            Place learner(s) at their assessed level
          </Label>
        </CheckboxContainer>
        <span className="text-xs text-muted-foreground">
          Automatically place learner(s) at their assessed level after the test.
        </span>
      </InputContainer>
    </div>
  );
}

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
